import React, { useState, useEffect } from 'react';
import Axios from 'axios';
import { Helmet } from 'react-helmet-async';

import {
  PageContainer,
  PageHero,
  Section,
  InfoRow,
  Protected,
} from '../theme/primitives';

const key = 'dqweqwe123y97qhduiasd$@^!*#&bjasdja&^&@!#';

const services = [
  { title: 'Consultants', desc: 'Contacts of consultants across Architectural, Structural, Interior Designing, Ground-water Survey, and more.' },
  { title: 'Contractors', desc: 'Civil, Electrical, Fabrication, Road-laying, Painting, Plumbing, Centering, and more.' },
  { title: 'Material & Machinery Suppliers', desc: 'Regularly updated database of suppliers and sources of materials and machinery for construction projects.' },
  { title: 'Testing Laboratories', desc: 'Quality control labs and standards information for material testing - Govt. and major private projects.' },
  { title: 'Engineers', desc: 'Experienced, Retired, and Fresher Engineers across Civil, Electrical, and Mechanical domains for project engagement.' },
  { title: 'Workforce', desc: 'Masons, Plumbers, Electricians, Painters, Carpenters, and Manpower Suppliers.' },
];

const features = [
  { title: 'Engineering Info', desc: 'Construction norms, Govt. rates & standard data, registration procedures, quality control, and industry standards - updated regularly.' },
  { title: 'Trends', desc: 'Latest practices, innovations, and trends shaping the construction industry.' },
  { title: 'Conversions', desc: 'Unit conversion calculator for length, area, weight, volume, stress, velocity, discharge, moment, power, pressure, and temperature.' },
  { title: 'Careers', desc: 'Latest job openings in both public and private sectors across the construction industry.' },
];

/* glyph for service cards */
const SvcIcon = ({ idx }) => {
  const icons = [
    // Consultants
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" key="0"><path d="M9 11.5a4 4 0 1 1 8 0c0 2.5-3 4-4 5.5"/><circle cx="13" cy="20" r=".75" fill="currentColor"/></svg>,
    // Contractors (hard hat)
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" key="1"><path d="M3 18h18M5 18a7 7 0 0 1 14 0M9 18V8M15 18V8M9 8h6"/></svg>,
    // Suppliers (truck)
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" key="2"><rect x="1" y="6" width="14" height="11" rx="1"/><path d="M15 11h4l3 3v3h-7"/><circle cx="6" cy="18.5" r="1.7"/><circle cx="17.5" cy="18.5" r="1.7"/></svg>,
    // Lab (flask)
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" key="3"><path d="M9 3h6M10 3v6L4 19a2 2 0 0 0 2 3h12a2 2 0 0 0 2-3l-6-10V3"/></svg>,
    // Engineers
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" key="4"><circle cx="12" cy="8" r="4"/><path d="M5 21a7 7 0 0 1 14 0"/></svg>,
    // Workforce (hammer)
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" key="5"><path d="m15 12-8.5 8.5a2.121 2.121 0 1 1-3-3L12 9"/><path d="M17.64 15 22 10.64"/><path d="m20.91 11.7-1.25-1.25c-.6-.6-.93-1.4-.93-2.25v-.86L16.01 4.6a5.56 5.56 0 0 0-3.94-1.64h-1.64a5.56 5.56 0 0 0-3.94 1.64L4.6 6.49"/></svg>,
  ];
  return <span className="block h-5 w-5">{icons[idx % icons.length]}</span>;
};

export const AboutUs = () => {
  const [metadata, setMetadata] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    Axios.post('/new_apis/getsinglemetadata', { type: 'about' }, { headers: { key } }).then((res) => {
      if (res.data.status) setMetadata(res.data.metadata[0]);
      else setMetadata({ seo_title: 'About ESAY' });
    });
  }, []);

  const handleCopy = (e) => e.preventDefault();

  return (
    <Protected onCopy={handleCopy}>
      {metadata && (
        <Helmet>
          <title>{metadata.seo_title}</title>
          {metadata.seo_desc && <meta name="description" content={metadata.seo_desc} />}
          {metadata.seo_keywords && <meta name="keywords" content={metadata.seo_keywords} />}
        </Helmet>
      )}

      <PageContainer maxWidth="lg">
        <PageHero
          compact
          kicker="Who we are"
          title="About ESAY"
          subtitle="Engineers with over 35 years of combined experience in construction - building the directory of contacts and information that powers India's project workflows."
        />

        {/* Mission card */}
        <Section>
          <div
            className="
              relative isolate overflow-hidden rounded-2xl
              border border-amber/24 bg-surface-paper
              p-5 sm:p-7
            "
          >
            <div
              aria-hidden
              className="pointer-events-none absolute -top-20 -right-20 h-64 w-64 rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(251,191,36,0.16), transparent 65%)' }}
            />
            <span aria-hidden className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-amber/60 to-transparent" />
            <div className="relative flex flex-col sm:flex-row sm:items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-amber/15 text-amber border border-amber/30">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6"><path d="M12 2 4 7l8 5 8-5-8-5z"/><path d="m4 17 8 5 8-5"/><path d="m4 12 8 5 8-5"/></svg>
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-[16px] font-bold tracking-[-0.01em] text-fg-primary">Our mission</h2>
                <div aria-hidden className="mt-1.5 h-[2px] w-10 rounded-full bg-gradient-to-r from-amber to-amber/0" />
                <p className="mt-3 text-[14px] leading-[1.7] text-fg-secondary">
                  ESAY is built by a group of engineers with over 35 years of combined experience across Government and Private construction. The portal has an <strong className="font-bold text-amber">ever-expanding database of contacts</strong> in the construction sector - instrumental for your project - alongside the latest construction-related information.
                </p>
              </div>
            </div>
          </div>
        </Section>

        <Section kicker="What we offer" title="Our services">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {services.map((s, i) => (
              <article
                key={s.title}
                className="
                  group relative overflow-hidden rounded-2xl
                  border border-border-subtle bg-surface-paper
                  p-4 sm:p-5
                  transition-[border-color,transform,box-shadow] duration-200 ease-out-soft
                  hover:border-amber/40 hover:-translate-y-1 hover:shadow-card-hover
                "
              >
                <span aria-hidden className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-amber/40 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber/10 text-amber border border-amber/20 transition-colors duration-200 group-hover:bg-amber/15 group-hover:border-amber/40">
                  <SvcIcon idx={i} />
                </div>
                <h3 className="mt-3 text-[14.5px] font-bold tracking-[-0.01em] text-fg-primary transition-colors duration-200 group-hover:text-amber">{s.title}</h3>
                <p className="mt-1 text-[12.5px] leading-[1.6] text-fg-secondary">{s.desc}</p>
              </article>
            ))}
          </div>
        </Section>

        <Section kicker="More from ESAY" title="Platform features">
          <div className="rounded-2xl border border-border-subtle bg-surface-paper px-4 sm:px-6 py-3">
            {features.map((f) => (
              <InfoRow key={f.title} label={f.title} value={f.desc} />
            ))}
          </div>
        </Section>
      </PageContainer>
    </Protected>
  );
};
